import { CalendarIcon, PencilIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import {
  planningConfigApi,
  PlanningConfiguration,
  PlanningCycle,
  planningCyclesApi,
} from "../services/planningCycles";

interface PlanningManagementProps {
  organizationId: string;
  orgRole: string;
  organization: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

const cycleTypes = [
  { value: "30-day", label: "Monthly", description: "Monthly planning cycles" },
  {
    value: "quarter",
    label: "Quarterly",
    description: "Quarterly planning cycles",
  },
  { value: "year", label: "Yearly", description: "Annual planning cycles" },
  {
    value: "3-year",
    label: "3-Year",
    description: "Multi-year strategic planning",
  },
];

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const PlanningManagement: React.FC<PlanningManagementProps> = ({
  organizationId,
  orgRole,
}) => {
  const [cycles, setCycles] = useState<PlanningCycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<PlanningCycle | null>(
    null,
  );
  const [config, setConfig] = useState<PlanningConfiguration | null>(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [selectedCycleTypes, setSelectedCycleTypes] = useState<string[]>([]);
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(1);
  const [currentViewYear, setCurrentViewYear] = useState(
    new Date().getFullYear(),
  ); // Current year being viewed

  const isAdmin = orgRole === "admin";

  useEffect(() => {
    if (organizationId) {
      fetchData();
    }
  }, [organizationId, currentViewYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const configData = await planningConfigApi.getConfig(organizationId);

      setConfig(configData);
      setSelectedCycleTypes(configData.cycle_types || []);
      setFiscalYearStartMonth(configData.fiscal_year_start_month || 1);

      // Generate cycles for current view year if configuration exists
      if (configData.cycle_types && configData.cycle_types.length > 0) {
        await generateCyclesForYear(currentViewYear);
      }
    } catch (error: any) {
      // console.error removed
    } finally {
      setLoading(false);
    }
  };

  const generateCyclesForYear = async (year: number) => {
    try {
      // Generate cycles for the specified year
      await planningConfigApi.generateCycles(organizationId, year);

      // Fetch all cycles and filter for the current view year
      const allCycles = await planningCyclesApi.listCycles(organizationId);
      const yearCycles = filterCyclesForYear(allCycles, year);
      // Filtered cycles logging removed
      setCycles(yearCycles);

      // Auto-select active cycle or first cycle
      const activeCycle = yearCycles.find((c) => c.is_active) || yearCycles[0];
      if (activeCycle) {
        setSelectedCycle(activeCycle);
      }
    } catch (error: any) {
      // console.error removed
    }
  };

  const filterCyclesForYear = (
    cycles: PlanningCycle[],
    year: number,
  ): PlanningCycle[] => {
    return cycles.filter((cycle) => {
      if (cycle.cycle_type === "3-year") {
        // "2025-2028" - show if year is in range
        const yearRange = cycle.name.split("-").map(Number);
        return year >= yearRange[0] && year <= yearRange[1];
      } else if (cycle.cycle_type === "year") {
        // "2025" or "2025/26" - show if starts with the year
        const cycleYear = cycle.name.includes("/")
          ? parseInt(cycle.name.split("/")[0])
          : parseInt(cycle.name);
        return cycleYear === year;
      } else if (
        cycle.cycle_type === "quarter" ||
        cycle.cycle_type === "30-day"
      ) {
        // For quarters and monthly cycles, check if they belong to the fiscal year
        const cycleDate = new Date(cycle.start_date);
        const _cycleMonth = cycleDate.getMonth() + 1; // getMonth() is 0-indexed
        const cycleYear = cycleDate.getFullYear();

        if (fiscalYearStartMonth === 1) {
          // Calendar year - simple case
          return cycleYear === year;
        } else {
          // Fiscal year - check if cycle falls within the fiscal year
          // Fiscal year 2025 with May start = May 2025 to April 2026
          const fiscalYearStart = new Date(year, fiscalYearStartMonth - 1, 1);
          const fiscalYearEnd = new Date(year + 1, fiscalYearStartMonth - 1, 0); // Last day of previous month

          return cycleDate >= fiscalYearStart && cycleDate <= fiscalYearEnd;
        }
      }
      return false;
    });
  };

  const handleUpdateConfig = async () => {
    if (!isAdmin) return;

    // Check if there are significant changes
    const removingCycleTypes = config?.cycle_types?.some(
      (type) => !selectedCycleTypes.includes(type),
    );
    const changingFiscalYear =
      config?.fiscal_year_start_month !== fiscalYearStartMonth;

    if (removingCycleTypes || changingFiscalYear) {
      const confirmMessage =
        `Warning: Changing the configuration will affect existing planning cycles.\n\n` +
        `${removingCycleTypes ? "• Removing cycle types may affect existing cycles\n" : ""}` +
        `${changingFiscalYear ? "• Changing fiscal year start will update all cycle dates to align with the new fiscal year\n" : ""}` +
        `\nDo you want to continue?`;

      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setSaving(true);

      const configUpdate = {
        cycle_types: selectedCycleTypes,
        fiscal_year_start_month: fiscalYearStartMonth,
      };

      // console.log removed
      await planningConfigApi.updateConfig(organizationId, configUpdate);
      // console.log removed

      // Update local config state
      setConfig({
        ...(config || { available_cycle_types: [] }),
        cycle_types: selectedCycleTypes,
        fiscal_year_start_month: fiscalYearStartMonth,
      });

      // Refetch the configuration to ensure we have the latest from backend
      const updatedConfig = await planningConfigApi.getConfig(organizationId);
      setConfig(updatedConfig);
      setSelectedCycleTypes(updatedConfig.cycle_types || []);
      setFiscalYearStartMonth(updatedConfig.fiscal_year_start_month || 1);

      // Generate cycles for current view year
      await generateCyclesForYear(currentViewYear);

      setShowConfigForm(false);
      // console.log removed
    } catch (error: any) {
      // console.error removed
      // console.error removed
      alert(
        "Failed to update configuration. Please check the console for details.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCycleTypeToggle = (cycleType: string) => {
    if (selectedCycleTypes.includes(cycleType)) {
      setSelectedCycleTypes(selectedCycleTypes.filter((t) => t !== cycleType));
    } else {
      setSelectedCycleTypes([...selectedCycleTypes, cycleType]);
    }
  };

  const groupCyclesByYear = (cycles: PlanningCycle[]) => {
    const grouped: { [key: string]: { [key: string]: PlanningCycle[] } } = {};

    cycles.forEach((cycle) => {
      let year: string;

      // Extract year from cycle name
      if (cycle.cycle_type === "3-year") {
        // "2025-2028" -> "2025-2028"
        year = cycle.name;
      } else if (cycle.cycle_type === "year") {
        // "2025" or "2025/26" -> "2025"
        year = cycle.name.includes("/") ? cycle.name.split("/")[0] : cycle.name;
      } else if (cycle.cycle_type === "quarter") {
        // For quarters, also consider fiscal year
        const cycleDate = new Date(cycle.start_date);
        const cycleMonth = cycleDate.getMonth() + 1;
        const cycleYear = cycleDate.getFullYear();

        if (fiscalYearStartMonth === 1) {
          // Calendar year - use year from name
          const yearPart = cycle.name.split(" ")[1] || "2025";
          year = yearPart.includes("/") ? yearPart.split("/")[0] : yearPart;
        } else {
          // Fiscal year - determine based on start date
          if (cycleMonth < fiscalYearStartMonth) {
            year = (cycleYear - 1).toString();
          } else {
            year = cycleYear.toString();
          }
        }
      } else if (cycle.cycle_type === "30-day") {
        // For monthly cycles, determine fiscal year based on start date
        const cycleDate = new Date(cycle.start_date);
        const cycleMonth = cycleDate.getMonth() + 1; // getMonth() is 0-indexed
        const cycleYear = cycleDate.getFullYear();

        if (fiscalYearStartMonth === 1) {
          // Calendar year
          year = cycleYear.toString();
        } else {
          // Fiscal year - if month is before fiscal start, it belongs to previous fiscal year
          if (cycleMonth < fiscalYearStartMonth) {
            year = (cycleYear - 1).toString();
          } else {
            year = cycleYear.toString();
          }
        }
      } else {
        // Fallback for unknown cycle types
        year = "2025";
      }

      if (!grouped[year]) {
        grouped[year] = {};
      }

      if (!grouped[year][cycle.cycle_type]) {
        grouped[year][cycle.cycle_type] = [];
      }

      grouped[year][cycle.cycle_type].push(cycle);
    });

    // Sort cycles within each type
    Object.keys(grouped).forEach((year) => {
      Object.keys(grouped[year]).forEach((type) => {
        grouped[year][type].sort(
          (a, b) =>
            new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
        );
      });
    });

    return grouped;
  };

  const getCycleTypeLabel = (type: string) => {
    const typeObj = cycleTypes.find((t) => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Planning Configuration
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure planning cycles for your organization
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Year Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentViewYear(currentViewYear - 1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="Previous year"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-lg font-medium text-gray-900 min-w-[80px] text-center">
              {currentViewYear}
            </span>
            <button
              onClick={() => setCurrentViewYear(currentViewYear + 1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="Next year"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {isAdmin && (
            <button
              onClick={() => setShowConfigForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
            >
              <PencilIcon className="w-4 h-4" />
              Configure Planning
            </button>
          )}
        </div>
      </div>

      {/* Planning Configuration */}
      {showConfigForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Planning Configuration</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Planning Cycle Types
              </label>
              <div className="space-y-2">
                {cycleTypes.map((type) => (
                  <label key={type.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedCycleTypes.includes(type.value)}
                      onChange={() => handleCycleTypeToggle(type.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {type.label}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        {type.description}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fiscal Year Start Month
              </label>
              <select
                value={fiscalYearStartMonth}
                onChange={(e) =>
                  setFiscalYearStartMonth(Number(e.target.value))
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                {monthNames.map((month, index) => (
                  <option key={index + 1} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              onClick={handleUpdateConfig}
              disabled={saving || selectedCycleTypes.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
            >
              {saving ? "Updating..." : "Update Configuration"}
            </button>
            <button
              onClick={() => {
                setShowConfigForm(false);
                setSelectedCycleTypes(config?.cycle_types || []);
                setFiscalYearStartMonth(config?.fiscal_year_start_month || 1);
              }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Planning Cycles */}
      {!showConfigForm && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {config?.cycle_types && config.cycle_types.length > 0 ? (
              <div className="space-y-6">
                {/* 3-Year Cycles - Show at top level */}
                {config?.cycle_types?.includes("3-year") && (
                  <div>
                    <h3 className="text-lg font-medium mb-4">
                      Strategic Planning Cycles
                    </h3>
                    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                      {cycles
                        .filter((c) => c.cycle_type === "3-year")
                        .map((cycle) => (
                          <div
                            key={cycle.id}
                            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                              selectedCycle?.id === cycle.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() => {
                              setSelectedCycle(cycle);
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium text-base">
                                    {cycle.name}
                                  </h4>
                                  {cycle.is_active && (
                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md">
                                      Current
                                    </span>
                                  )}
                                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md">
                                    Strategic
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  {formatDate(cycle.start_date)} -{" "}
                                  {formatDate(cycle.end_date)}
                                </div>
                                {cycle.days_remaining !== undefined &&
                                  cycle.days_remaining >= 0 && (
                                    <div className="text-sm text-gray-500 mt-1">
                                      {cycle.days_remaining} days remaining
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Year-Specific Cycles */}
                <div>
                  <h3 className="text-lg font-medium mb-4">
                    Planning Cycles for{" "}
                    {fiscalYearStartMonth !== 1
                      ? `Fiscal Year ${currentViewYear}/${(currentViewYear + 1).toString().slice(2)}`
                      : currentViewYear}
                  </h3>
                  <div className="space-y-6">
                    {/* Show cycle types in order: year, quarter, monthly */}
                    {["year", "quarter", "30-day"].map((cycleType) => {
                      const cycleTypeData =
                        groupCyclesByYear(cycles)[currentViewYear.toString()];
                      if (
                        !cycleTypeData ||
                        !cycleTypeData[cycleType] ||
                        cycleTypeData[cycleType].length === 0
                      )
                        return null;

                      return (
                        <div key={cycleType}>
                          <h4 className="font-medium text-gray-700 mb-3">
                            {getCycleTypeLabel(cycleType)} (
                            {cycleTypeData[cycleType].length})
                          </h4>
                          <div
                            className={`grid gap-3 ${
                              cycleType === "30-day"
                                ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
                                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                            }`}
                          >
                            {cycleTypeData[cycleType].map((cycle) => (
                              <div
                                key={cycle.id}
                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                  selectedCycle?.id === cycle.id
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-200 hover:border-gray-300"
                                }`}
                                onClick={() => {
                                  setSelectedCycle(cycle);
                                }}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h5 className="font-medium text-sm">
                                        {cycle.name}
                                      </h5>
                                      {cycle.is_active && (
                                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md">
                                          Current
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {formatDate(cycle.start_date)} -{" "}
                                      {formatDate(cycle.end_date)}
                                    </div>
                                    {cycle.days_remaining !== undefined &&
                                      cycle.days_remaining >= 0 && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          {cycle.days_remaining} days remaining
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  No Planning Configuration
                </h4>
                <p className="text-gray-600 mb-4">
                  Configure which planning cycle types your organization uses to
                  get started.
                </p>
                {isAdmin && (
                  <button
                    onClick={() => setShowConfigForm(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                  >
                    Configure Planning
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningManagement;
